import React, { useState } from 'react';
import { IconButton, Menu, Badge, Box, Typography, Link, CircularProgress, Divider } from '@mui/material';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import DOMPurify from 'dompurify';
import { useNotifications } from '../../hooks/useNotifications';
import { Notification, NotificationType } from '../../types/notification';
import { NOTIFICATION_CONSTANTS } from '../../constants/notifications';

const typeConfig: Record<NotificationType, {
  icon: React.ReactNode;
  accent: string;
  bg: string;
  label: string;
}> = {
  info: {
    icon: <InfoOutlinedIcon sx={{ fontSize: 18 }} />,
    accent: '#2563eb',
    bg: '#eff6ff',
    label: 'Update',
  },
  success: {
    icon: <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />,
    accent: '#16a34a',
    bg: '#f0fdf4',
    label: 'Success',
  },
  warning: {
    icon: <WarningAmberIcon sx={{ fontSize: 18 }} />,
    accent: '#d97706',
    bg: '#fffbeb',
    label: 'Attention',
  },
  feature: {
    icon: <AutoAwesomeIcon sx={{ fontSize: 18 }} />,
    accent: '#7c3aed',
    bg: '#f5f3ff',
    label: 'New Feature',
  },
};

interface NotificationDropdownProps {
  color?: string;
  /** 'below' (default) opens under the bell, right-aligned; 'right' opens beside it, for the left rail. */
  menuPlacement?: 'below' | 'right';
  /** Visible word beside the bell (the rail's "Alerts"); it is part of the button, so the whole row is the tap target. */
  label?: string;
  /** With a label: stack the word under the bell (collapsed rail) instead of beside it. */
  stacked?: boolean;
}

const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  color = 'hsl(var(--muted-foreground))',
  menuPlacement = 'below',
  label,
  stacked = false,
}) => {
  const { notifications, loading, dismissNotification } = useNotifications();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDismiss = (id: string, event: React.MouseEvent) => {
    event.stopPropagation();
    dismissNotification(id);
  };

  return (
    <>
      <IconButton 
        size="small" 
        style={{ color }} 
        onClick={handleClick}
        aria-label={label ? `${label}, ${notifications.length} notifications` : `${notifications.length} notifications`}
        sx={{
          // 44px minimum tap target (WCAG 2.5.8).
          minWidth: 44,
          minHeight: 44,
          ...(label && {
            width: '100%',
            minHeight: 48,
            borderRadius: 'var(--radius-sm)',
            flexDirection: stacked ? 'column' : 'row',
            justifyContent: stacked ? 'center' : 'flex-start',
            gap: stacked ? 0.25 : 1.5,
            px: stacked ? 0.5 : 1.5,
            fontSize: stacked ? '0.66rem' : '0.85rem',
            lineHeight: 1.2,
          }),
        }}
      >
        <Badge 
          badgeContent={notifications.length} 
          max={NOTIFICATION_CONSTANTS.MAX_BADGE_COUNT}
          sx={{
            '& .MuiBadge-badge': {
              backgroundColor: '#ef4444',
              color: '#fff',
              fontSize: '0.7rem',
              fontWeight: 700,
              minWidth: 18,
              height: 18,
            },
          }}
        >
          <NotificationsNoneIcon />
        </Badge>
        {label}
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: NOTIFICATION_CONSTANTS.DROPDOWN_WIDTH_PX,
            maxHeight: NOTIFICATION_CONSTANTS.DROPDOWN_MAX_HEIGHT_PX,
            mt: 1,
            padding: 0,
            boxShadow: '0 20px 60px -15px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(0, 0, 0, 0.05)',
            borderRadius: '12px',
            overflowX: 'hidden',
            overflowY: 'auto',
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
            '& .MuiList-root': { padding: 0 },
            '& .MuiMenuItem-root': { padding: 0 },
          },
        }}
        transformOrigin={
          menuPlacement === 'right' ? { horizontal: 'left', vertical: 'bottom' } : { horizontal: 'right', vertical: 'top' }
        }
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* Header */}
        <Box sx={{ 
          px: 2.5, 
          py: 2, 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #e5e7eb',
          position: 'sticky',
          top: 0,
          backgroundColor: '#ffffff',
          zIndex: 10,
        }}>
          <Typography 
            sx={{ 
              fontSize: '0.9375rem',
              fontWeight: 700,
              color: '#111827',
              letterSpacing: '-0.01em',
            }}
          >
            Notifications
          </Typography>
          {notifications.length > 0 && (
            <Box
              sx={{
                backgroundColor: '#ef4444',
                color: '#fff',
                fontSize: '0.7rem',
                fontWeight: 700,
                borderRadius: '10px',
                px: 1,
                py: 0.25,
                lineHeight: 1.5,
                minWidth: 20,
                textAlign: 'center',
              }}
            >
              {notifications.length}
            </Box>
          )}
        </Box>

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, gap: 1.5 }}>
            <CircularProgress size={24} sx={{ color: '#9ca3af' }} />
            <Typography sx={{ color: '#9ca3af', fontSize: '0.8125rem' }}>
              Loading...
            </Typography>
          </Box>
        )}

        {/* Empty State */}
        {!loading && notifications.length === 0 && (
          <Box sx={{ px: 3, py: 6, textAlign: 'center' }}>
            <InboxOutlinedIcon sx={{ fontSize: 40, color: '#d1d5db', mb: 1.5 }} />
            <Typography 
              sx={{ 
                color: '#6b7280',
                fontSize: '0.875rem',
                fontWeight: 500,
              }}
            >
              You&apos;re all caught up
            </Typography>
            <Typography 
              sx={{ 
                color: '#9ca3af',
                fontSize: '0.8125rem',
                mt: 0.5,
              }}
            >
              No new notifications
            </Typography>
          </Box>
        )}

        {/* Notifications List */}
        {!loading && notifications.length > 0 && (
          <Box>
            {notifications.map((notification, index) => (
              <React.Fragment key={notification.id}>
                {index > 0 && <Divider sx={{ borderColor: '#f3f4f6' }} />}
                <NotificationItem
                  notification={notification}
                  onDismiss={handleDismiss}
                />
              </React.Fragment>
            ))}
          </Box>
        )}
      </Menu>
    </>
  );
};

interface NotificationItemProps {
  notification: Notification;
  onDismiss: (id: string, event: React.MouseEvent) => void;
}

const NotificationItem: React.FC<NotificationItemProps> = ({ notification, onDismiss }) => {
  const config = typeConfig[notification.type] || typeConfig.info;

  const getImageUrl = (url?: string) => {
    if (!url) return '';
    if (url.startsWith('/')) return url;
    if (url.includes('execute-api') && url.includes('/notifications/images/')) {
      const filename = url.split('/').pop();
      return `/notifications/images/${filename}`;
    }
    return url;
  };

  const processedImageUrl = getImageUrl(notification.imageUrl);

  const timeAgo = (dateStr: string) => {
    const now = new Date();
    const date = new Date(dateStr);
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <Box
      sx={{
        px: 2.5,
        py: 2,
        backgroundColor: '#ffffff',
        transition: 'background-color 0.15s ease',
        '&:hover': {
          backgroundColor: '#f9fafb',
        },
        position: 'relative',
        cursor: 'default',
      }}
    >
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        {/* Type Icon */}
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '8px',
            backgroundColor: config.bg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            color: config.accent,
            mt: 0.25,
          }}
        >
          {config.icon}
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Top row: title/label + time + close */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'flex-start', 
            justifyContent: 'space-between',
            gap: 1,
            mb: 0.5,
          }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {notification.title ? (
                <Typography 
                  sx={{ 
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    color: '#111827',
                    lineHeight: 1.4,
                  }}
                  noWrap
                >
                  {DOMPurify.sanitize(notification.title, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] })}
                </Typography>
              ) : (
                <Typography 
                  sx={{ 
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: config.accent,
                    lineHeight: 1.4,
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em',
                  }}
                >
                  {config.label}
                </Typography>
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
              <Typography 
                sx={{ 
                  fontSize: '0.6875rem',
                  color: '#9ca3af',
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                }}
              >
                {timeAgo(notification.createdAt)}
              </Typography>
              <IconButton
                size="small"
                onClick={(e) => onDismiss(notification.id, e)}
                sx={{
                  padding: '2px',
                  color: '#d1d5db',
                  '&:hover': {
                    color: '#6b7280',
                    backgroundColor: '#f3f4f6',
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          </Box>

          {/* Message */}
          <Typography 
            sx={{ 
              fontSize: '0.8125rem',
              lineHeight: 1.5,
              color: '#6b7280',
              whiteSpace: 'pre-line',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {DOMPurify.sanitize(notification.message, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] })}
          </Typography>

          {/* Image */}
          {processedImageUrl && (
            <Box sx={{ 
              mt: 1.5, 
              borderRadius: '8px', 
              overflow: 'hidden',
              border: '1px solid #e5e7eb',
              lineHeight: 0,
            }}>
              <img 
                src={processedImageUrl} 
                alt="Update preview" 
                style={{ width: '100%', height: 'auto', display: 'block' }} 
              />
            </Box>
          )}

          {/* Link */}
          {notification.link && (
            <Link
              href={DOMPurify.sanitize(notification.link, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] })}
              target="_blank"
              rel="noopener noreferrer"
              underline="none"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                mt: 1,
                fontSize: '0.8125rem',
                color: config.accent,
                fontWeight: 600,
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              {DOMPurify.sanitize(
                notification.linkText || NOTIFICATION_CONSTANTS.DEFAULT_LINK_TEXT, 
                { ALLOWED_TAGS: [], ALLOWED_ATTR: [] }
              )}
              <OpenInNewIcon sx={{ fontSize: 12 }} />
            </Link>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default NotificationDropdown;
